import {
  Alert,
  Badge,
  Container,
  CopyToClipboard,
  Header,
  Link,
  SpaceBetween,
  KeyValuePairs,
  Button,
  StatusIndicator,
  Box,
  Modal,
  Tabs,
} from '@amzn/awsui-components-console';
import { useParams, useSearchParams } from 'react-router';
import { useState, useEffect } from 'react';
import type { EvaluationDetails } from '@amzn/helianthus-arbiter-client';
import { EvaluationStatus, ResourceNotFoundException } from '@amzn/helianthus-arbiter-client';
import { useArbiterClient } from '../../context/ArbiterClientContext';
import { ErrorState } from '../../components/ErrorState';
import { LoadingState } from '../../components/LoadingState';
import { AuditTrailTimeline } from '../../components/AuditTrailTimeline';
import { useEvaluationAuditTrail } from '../../hooks/useEvaluationAuditTrail';
import { getStatusType, getOutcomeType, formatSubject, getSubjectPresentation, groupResultsByRule, buildBindleUrl, OUTCOME_LABEL, UNKNOWN_SUBJECT, EVALUATION_STATUS_DISPLAY, type EvaluationOutcome } from '../../utils/evaluationHelpers';
import { RuleResultsPanel } from '../../components/RuleResultsPanel';
import { PerformanceMetricsPanel } from '../../components/PerformanceMetricsPanel';
import { AuditTrailExportButton } from '../../components/AuditTrailExportButton';
import { ArnDisplay } from '../../components/ArnDisplay';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { infoPopover } from '../../components/InfoPopover';
import { formatDateTime } from '../../utils/formatFieldName';

const REQUESTER_INFO: React.ReactNode = (
  infoPopover('Requester', "The principal that created this evaluation. Automation calls with an IAM role, shown as the role name and session; a person calling directly shows as their user alias. Select the value to see the full ARN. Evaluations created before Arbiter started returning this field have no requester and the row is hidden.")
);

const SOURCE_INFO: React.ReactNode = (
  infoPopover('Source', "The system that created this evaluation, as declared by the caller — for example helianthus-evaluation-service, fn-flywheel-validation or code-defender. It is self-reported and optional, so this row is absent when the caller did not declare one. Requester is always present and identifies the calling principal regardless.")
);

const SUBJECT_FIELD_INFO: Record<string, React.ReactNode> = {
  'Base Commit': (
    infoPopover('Base Commit', "The commit the diff was generated against — its base. It is not a commit containing the diff's changes; those exist only in the diff itself. Arbiter clones the package and checks it out at this revision so rules can read the surrounding file context the diff doesn't include.")
  ),
  Commit: (
    infoPopover('Commit', "The commit Arbiter scanned. An Auto-detected badge means the request named only a branch, so Arbiter detected the commit at its tip when it cloned the package; later pushes aren't part of this evaluation. Without the badge, it is the commit the request asked for. Until a commit is recorded the row reads Resolving; if the evaluation ended without one, Not determined.")
  ),
};

// ---------------------------------------------------------------------------
// Audit Trail Tab content
// ---------------------------------------------------------------------------

function AuditTrailTab({ evaluationId, enabled }: { evaluationId: string; enabled: boolean }): React.JSX.Element {
  const { auditTrail, loading, error, isNotFound } = useEvaluationAuditTrail(evaluationId, { enabled });

  if (!enabled) {
    return <Box textAlign="center" color="text-body-secondary" padding={{ top: 'l' }}>Select this tab to load the audit trail.</Box>;
  }

  if (loading) {
    return <LoadingState label="Loading audit trail..." />;
  }

  if (isNotFound) {
    return (
      <Alert type="info">
        Audit trail not available for this evaluation. Audit trails are generated
        for evaluations created after LookingGlass deployment.
      </Alert>
    );
  }

  if (error) {
    return (
      <ErrorState
        variant="api-error"
        title="Error loading audit trail"
        message={error.message}
      />
    );
  }

  if (!auditTrail || !auditTrail.auditEntries || auditTrail.auditEntries.length === 0) {
    return (
      <Alert type="info">
        No audit trail entries found for this evaluation.
      </Alert>
    );
  }

  return (
    <SpaceBetween size="l">
      <Box float="right">
        <AuditTrailExportButton auditTrail={auditTrail} evaluationId={evaluationId} />
      </Box>
      <PerformanceMetricsPanel entries={auditTrail.auditEntries} />
      <AuditTrailTimeline entries={auditTrail.auditEntries} />
    </SpaceBetween>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function EvaluationDetailPage() {
  const { evaluationId } = useParams<{ evaluationId: string }>();
  const client = useArbiterClient();
  const [evaluation, setEvaluation] = useState<EvaluationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // Coerced rather than passed through: `||` only catches an absent or empty param, so a stale or
  // typo'd ?tab would select no tab at all and leave the page blank.
  const activeTabId = searchParams.get('tab') === 'audit-trail' ? 'audit-trail' : 'overview';
  // Passed through as the API takes it; absent, the request is unchanged.
  const includeDryRunResults = searchParams.get('includeDryRunResults') === 'true';
  useDocumentTitle(evaluation ? `Evaluation: ${formatSubject(evaluation)}` : 'Evaluation');

  useEffect(() => {
    const fetchEvaluation = async () => {
      if (!evaluationId) return;

      try {
        setLoading(true);
        setError(null);
        const response = await client.describeEvaluation(
          includeDryRunResults ? { evaluationId, includeDryRunResults } : { evaluationId },
        );
        if (response.evaluation) setEvaluation(response.evaluation);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch evaluation'));
      } finally {
        setLoading(false);
      }
    };

    fetchEvaluation();
  }, [evaluationId, client, includeDryRunResults]);

  const handleCancel = async () => {
    if (!evaluationId) return;

    setCancelling(true);
    try {
      await client.cancelEvaluation({ evaluationId });
      const response = await client.describeEvaluation(
          includeDryRunResults ? { evaluationId, includeDryRunResults } : { evaluationId },
        );
      if (response.evaluation) setEvaluation(response.evaluation);
      setShowCancelModal(false);
    } catch (err) {
      console.error('Failed to cancel evaluation:', err);
      setError(err instanceof Error ? err : new Error('Failed to cancel evaluation'));
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return <LoadingState label="Loading evaluation..." />;
  }

  if (error) {
    const is404 = error instanceof ResourceNotFoundException;
    return (
      <ErrorState
        variant={is404 ? 'not-found' : 'api-error'}
        title={is404 ? 'Evaluation not found' : 'Error loading evaluation'}
        message={is404
          ? `The evaluation "${evaluationId}" could not be found. It may have been deleted.`
          : error.message
        }
        onRetry={!is404 ? () => window.location.reload() : undefined}
        backLabel="Back to Evaluations"
        backHref="/evaluations"
      />
    );
  }

  if (!evaluation) {
    return (
      <ErrorState
        variant="not-found"
        title="Evaluation not found"
        message={`The evaluation "${evaluationId}" could not be found.`}
        backLabel="Back to Evaluations"
        backHref="/evaluations"
      />
    );
  }

  const canCancel = evaluation.status === EvaluationStatus.IN_PROGRESS;

  const headerLabel = (() => {
    const subject = formatSubject(evaluation);
    if (subject && subject !== UNKNOWN_SUBJECT) {
      return `Evaluation: ${subject}`;
    }
    return `Evaluation: ${evaluation.evaluationId?.slice(0, 8) ?? 'Unknown'}`;
  })();

  const subjectPresentation = getSubjectPresentation(evaluation);

  const resultCount = evaluation.results?.length ?? 0;
  const ruleCount = evaluation.results ? groupResultsByRule(evaluation.results).length : 0;
  const resultsDescription = `${resultCount} result${resultCount === 1 ? '' : 's'} across ${ruleCount} rule${ruleCount === 1 ? '' : 's'}${includeDryRunResults ? ' (includes dry-run results)' : ''}`;

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            {canCancel && (
              <Button onClick={() => setShowCancelModal(true)}>
                Cancel Evaluation
              </Button>
            )}
          </SpaceBetween>
        }
      >
        {headerLabel}
      </Header>

      <Tabs
        activeTabId={activeTabId}
        onChange={({ detail }) => {
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (detail.activeTabId === 'overview') {
              next.delete('tab');
            } else {
              next.set('tab', detail.activeTabId);
            }
            return next;
          }, { replace: true });
        }}
        tabs={[
          {
            id: 'overview',
            label: 'Overview',
            content: (
              <SpaceBetween size="l">
                <Container
                  header={
                    <Header
                      variant="h2"
                      actions={
                        subjectPresentation.kind && (
                          <Badge color="blue">{subjectPresentation.typeLabel}</Badge>
                        )
                      }
                    >
                      Details
                    </Header>
                  }
                >
                  { }
                  <KeyValuePairs
                    columns={3}
                    items={[
                      {
                        type: 'group',
                        title: 'General',
                        items: [
                          {
                            label: 'Evaluation ID',
                            value: evaluation.evaluationId ? (
                              <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
                                <span>{evaluation.evaluationId}</span>
                                <CopyToClipboard
                                  variant="icon"
                                  textToCopy={`${window.location.origin}/evaluations/${evaluation.evaluationId}`}
                                  copySuccessText="Evaluation link copied"
                                  copyErrorText="Failed to copy"
                                  copyButtonAriaLabel={`Copy link to evaluation ${evaluation.evaluationId}`}
                                />
                              </SpaceBetween>
                            ) : undefined,
                          },
                          {
                            label: 'Status',
                            value: (
                              <StatusIndicator type={getStatusType(evaluation.status!)}>
                                {EVALUATION_STATUS_DISPLAY[evaluation.status!]}
                              </StatusIndicator>
                            ),
                          },
                          {
                            label: 'Bindle Lock',
                            value: evaluation.bindleLock ? (
                              <Link
                                href={buildBindleUrl(evaluation.bindleLock)}
                                external
                                externalIconAriaLabel="Opens in a new tab"
                              >
                                {evaluation.bindleLock}
                              </Link>
                            ) : undefined,
                          },
                          {
                            label: 'Result',
                            value: evaluation.outcome ? (
                              <StatusIndicator type={getOutcomeType(evaluation.outcome as EvaluationOutcome)}>
                                {OUTCOME_LABEL[evaluation.outcome] ?? evaluation.outcome}
                              </StatusIndicator>
                            ) : null,
                          },
                          // Conditionally spread rather than filtered below: ArnDisplay always
                          // renders an element, so the value is never null for the filter to drop.
                          ...(evaluation.requester ? [{
                            label: 'Requester',
                            value: <ArnDisplay value={evaluation.requester} />,
                            info: REQUESTER_INFO,
                          }] : []),
                          ...(evaluation.source ? [{
                            label: 'Source',
                            value: evaluation.source,
                            info: SOURCE_INFO,
                          }] : []),
                          ...(evaluation.snsTopic ? [{
                            label: 'SNS Topic',
                            value: evaluation.snsTopic,
                          }] : []),
                        ].filter(item => item.value != null && item.value !== '' && item.value !== '--'),
                      },

                      ...(subjectPresentation.fields.length > 0
                        ? [{
                            type: 'group' as const,
                            title: 'Subject',
                            items: subjectPresentation.fields.map((field) => {
                              const value = field.href ? (
                                <Link href={field.href} external externalIconAriaLabel="Opens in a new tab">
                                  {field.value}
                                </Link>
                              ) : (
                                field.value
                              );
                              return {
                                label: field.label,
                                value: field.badge ? (
                                  <SpaceBetween direction="horizontal" size="xs">
                                    {value}
                                    <Badge>{field.badge}</Badge>
                                  </SpaceBetween>
                                ) : (
                                  value
                                ),
                                info: SUBJECT_FIELD_INFO[field.label],
                              };
                            }),
                          }]
                        : []),

                      {
                        type: 'group',
                        title: 'Timing',
                        items: [
                          {
                            label: 'Start Time',
                            value: formatDateTime(evaluation.startTime),
                          },
                          ...(evaluation.endTime ? [{
                            label: 'End Time',
                            value: formatDateTime(evaluation.endTime),
                          }] : []),
                          ...(evaluation.executionTime != null ? [{
                            label: 'Execution Time',
                            value: `${evaluation.executionTime.toFixed(1)}s`,
                          }] : []),
                        ],
                      },
                    ]}
                  />
                </Container>

                {evaluation.results && evaluation.results.length > 0 && (

                  <Container header={
                    <Header
                      variant="h2"
                      description={resultsDescription}
                      info={
                        infoPopover('Results count', "Rules evaluated may differ from total outcomes. A single rule can produce multiple results, and some rules may be skipped based on applicability.")
                      }
                    >
                      Rules
                    </Header>
                  }>
                    <RuleResultsPanel results={evaluation.results} />
                  </Container>
                )}
              </SpaceBetween>
            ),
          },
          {
            id: 'audit-trail',
            label: 'Audit Trail',
            content: evaluationId ? (
              <AuditTrailTab
                evaluationId={evaluationId}
                // Always enabled: the trail prefetches on page mount (in
                // parallel with the evaluation fetch) so the tab renders
                // instantly instead of loading on first click.
                enabled
              />
            ) : null,
          },
        ]}
      />

      <Modal
        visible={showCancelModal}
        onDismiss={() => setShowCancelModal(false)}
        header="Cancel Evaluation"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowCancelModal(false)}>
                Close
              </Button>
              <Button
                variant="primary"
                onClick={handleCancel}
                loading={cancelling}
                disabled={cancelling}
              >
                Cancel Evaluation
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        Are you sure you want to cancel this evaluation? This action cannot be undone.
      </Modal>
    </SpaceBetween>
  );
}
